import React, { useRef, useState, useEffect } from "react";
import * as yup from "yup";
import { useLocation } from "react-router-dom";
import queryString from "query-string";
import { Report, TonnageDisclaimer } from "../../../shared";

const schema = yup.object({
  epaId: yup.string(),
  name: yup.string(),
  street: yup.string(),
  county: yup.string(),
  cesPct: yup.string(),
  city: yup.string(),
  zipCode: yup.string(),
  status: yup.string(),
  naics: yup.string(),
  exclude: yup.string(),
  issueFDate: yup.date(),
  issueTDate: yup.date().when('issueFDate', (issueFDate, schema) => ((issueFDate) && schema.min(issueFDate, "Issued To date must be later than the Issued From date."))),
});

function CompanyReport() {
  const location = useLocation();
  const params = queryString.parse(location.search);
  const searchOnInit = Object.keys(params).length > 0 ? true : false;
  const [initialFormValues, setInitialFormValues] = useState({
    addressType: params && params.addressType ? params.addressType : "physical",
    epaId: params && params.epaId ? params.epaId : "",
    name: params && params.name ? params.name : "",
    street: params && params.street ? params.street : "",
    city: params && params.city ? params.city : "",
    zipCode: params && params.zipCode ? params.zipCode : "",
    county: params && params.county ? params.county : "",
    multicespct: params && params.multicespct ? params.multicespct.split(",") : [],
    status: params && params.status ? params.status : "",
    epaIdType: params && params.epaIdType ? params.epaIdType : "",
    epaIdCategory: params && params.epaIdCategory ? params.epaIdCategory : "",
    multinaics: params && params.multinaics ? params.multinaics.split(",") : [],
    naics: params && params.naics ? params.naics : "",
    multiexclude: params && params.multiexclude ? params.multiexclude.split(",") : [],
    issueFDate: params && params.issueFDate ? params.issueFDate : "",
    issueTDate: params && params.issueTDate ? params.issueTDate : "",
    caOnly: params && params.caOnly ? params.caOnly : false,
  });
  const [backToUrl, setBacktoUrl] = useState("/reports");
  const [pageTitle, setPageTitle] = useState("Search for a Handler (Advanced)");
  const [apiEndpoint, setApiEndpoint] = useState(
    "/api/v1/hwts/search/facility/advanced"
  );
  const BASE_API_URI = process.env.REACT_APP_B2C_POST_LOGOUT_URI;
  const defaultExportParams = {
    allColumns: true,
    processCellCallback: params => {
      const field = params.column.getColDef().field;
      return field === 'EPA_ID' ? `=HYPERLINK("${BASE_API_URI}/facility/${params.value}","${params.value}")` : params.value;
    }
  };

  const formConfig = [
    {
      name: "epaId",
      config: {
        col: "md",
        colWidth: "3",
        label: "ID Number",
        placeholder: "",
        type: "text",
      },
    },
    {
      name: "name",
      config: {
        col: "md",
        colWidth: "9",
        label: "Handler Name",
        placeholder: "",
        type: "text",
      },
    },
    {
      name: "addressType",
      config: {
        col: "md",
        colWidth: "3",
        label: "Address Type",
        placeholder: "",
        type: "select3",
        data: [
          { label: "All", value: "all" },
          { label: "Physical Address", value: "physical" },
          { label: "Mailing Address", value: "mailing" },
          { label: "Owner Address", value: "owner" },
          { label: "Operator Address", value: "operator" },
        ],
      },
    },
    {
      name: "street",
      config: {
        col: "md",
        colWidth: "4",
        label: "Street",
        placeholder: "",
        type: "text",
      },
    },
    // {
    //   name: "city",
    //   config: {
    //     col: "md",
    //     colWidth: "3",
    //     label: "City",
    //     placeholder: "Select a City",
    //     errorMessage: "A city is required.",
    //     type: "select3",
    //     apiEndpoint: "api/v1/hwts/search/cities",
    //     hasApi: true,
    //   },
    // },
    {
      name: "city",
      config: {
        col: "md",
        colWidth: "3",
        label: "City",
        placeholder: "",
        type: "text",
      },
    },
    {
      name: "zipCode",
      config: {
        label: "Zip Code",
        col: "md",
        colWidth: "2",
        placeholder: "",
        type: "text",
      },
    },
    {
      name: "county",
      config: {
        label: "County",
        col: "md",
        validate: true,
        colWidth: "4",
        placeholder: "Select a County",
        errorMessage: "A county is required.",
        type: "select3",
        apiEndpoint: "api/v1/hwts/search/counties",
        hasApi: true,
      },
    },
    {
      name: "multicespct",
      config: {
        label: "CalEnviroScreen Score Percentile",
        col: "md",
        validate: true,
        colWidth: "4",
        placeholder: "",
        type: "multi-select",
        apiEndpoint: "api/v1/hwts/search/ces-pct",
        hasApi: true,
        customStyles: {
          control: (base) => ({
            ...base,
            borderRadius: 0,
            fontSize: "1rem",
          }),
          menu: (base) => ({
            ...base,
            width: 800
          })
        },
        advanced: true,
      },
    },
    {
      name: "status",
      config: {
        col: "md",
        colWidth: "2",
        label: "ID Status",
        placeholder: "",
        type: "select3",
        data: [
          { value: "active", label: "Active" },
          { value: "inactive", label: "Inactive" },
        ],
      },
    },
    {
      name: "epaIdType",
      config: {
        col: "md",
        colWidth: "2",
        label: "ID Type",
        placeholder: "",
        type: "select3",
        data: [
          { value: "p", label: "Permanent" },
          { value: "t", label: "Temporary" },
        ],
      },
    },
    {
      name: "epaIdCategory",
      config: {
        col: "md",
        colWidth: "2",
        label: "ID Category",
        placeholder: "",
        type: "select3",
        data: [
          { value: "federal", label: "Federal" },
          { value: "state", label: "State" },
        ],
      },
    },
    {
      name: "entityType",
      config: {
        col: "md",
        colWidth: "2",
        label: "Entity Type",
        placeholder: "",
        type: "select3",
        data: [
          { value: "generator", label: "Generator" },
          { value: "transporter", label: "Transporter" },
          { value: "tsdf", label: "TSDF" },
        ],
      },
    },
    // {
    //   name: "naics",
    //   config: {
    //     col: "md",
    //     colWidth: "2",
    //     label: "NAICS Code",
    //     placeholder: "",
    //     type: "text",
    //   },
    // },
    {
      name: "multinaics",
      config: {
        label: "NAICS Code",
        col: "md",
        validate: true,
        colWidth: "4",
        placeholder: "Select a NAICS Code",
        errorMessage: "An NAICS Code is required.",
        type: "multi-select",
        apiEndpoint: "api/v1/hwts/tempid/naics",
        hasApi: true,
        customStyles: {
          control: (base) => ({
            ...base,
            borderRadius: 0,
            fontSize: "1rem",
          }),
          menu: (base) => ({
            ...base,
            width: 800
          })
        },
        advanced: true,
      },
    },
    {
      name: "multiexclude",
      config: {
        col: "md",
        colWidth: "4",
        label: "Exclude",
        placeholder: "",
        type: "multi-select",
        data: [
          { value: "cac", label: "CAC - State Provisional or Emergency Number" },
          { value: "cah", label: "CAH - Household Hazardous Waste" },
          { value: "cap", label: "CAP - Federal Provisional or Emergency Number" },
          { value: "cas", label: "CAS - State Emergency Response" },
          { value: "clu", label: "CLU - Clandestine Drug Lab" },
        ],
        customStyles: {
          control: (base) => ({
            ...base,
            borderRadius: 0,
            fontSize: "1rem",
          }),
          menu: (base) => ({
            ...base,
            width: 450
          })
        },
        advanced: true,
      },
    },
    {
      name: "issueFDate",
      config: {
        col: "md",
        colWidth: "2",
        label: "Issued From",
        placeholder: "",
        type: "date",
        advanced: true,
      },
    },
    {
      name: "issueTDate",
      config: {
        col: "md",
        colWidth: "2",
        label: "Issued To",
        placeholder: "",
        type: "date",
        advanced: true,
      },
    },
    {
      name: "caOnly",
      config: {
        col: "md",
        colWidth: "2",
        label: "CA Only?",
        placeholder: "",
        type: "select3",
        data: [
          { label: "Yes", value: true },
          { label: "No", value: false },
        ],
        advanced: true,
      },
    },
  ];

  const customGridCells = [
    {
      columnName: "EPA_ID",
      cellRenderer: function (params) {
        if (params.value != null) {
          return (
            `<a href="/facility/${params.value}" target="_">` +
            params.value +
            "</a>"
          );
        }
      },
    },
    {
      columnName: "Mail_Street",
      hide: true,
    },
    {
      columnName: "Mail_City",
      hide: true,
    },
    {
      columnName: "Mail_St",
      hide: true,
    },
    {
      columnName: "Mail_Zip",
      hide: true,
    },
    {
      columnName: "Owner_Name",
      hide: true,
    },
    {
      columnName: "Owner_Street",
      hide: true,
    },
    {
      columnName: "Owner_City",
      hide: true,
    },
    {
      columnName: "Owner_State",
      hide: true,
    },
    {
      columnName: "Owner_Zip",
      hide: true,
    },
    {
      columnName: "Site_Contact_Name",
      hide: true,
    },
    {
      columnName: "Site_Contact_Street",
      hide: true,
    },
    {
      columnName: "Site_Contact_City",
      hide: true,
    },
    {
      columnName: "Site_Contact_State",
      hide: true,
    },
    {
      columnName: "Site_Contact_Zip",
      hide: true,
    },
  ];

  return (
    <Report
      breadcrumb={[
        { name: "HWTS Reports", url: "/reports", active: false },
        { name: "Handler Search", url: "", active: true },
      ]}
      backToUrl={backToUrl}
      pageTitle={pageTitle}
      initialFormValues={initialFormValues}
      schema={schema}
      formConfig={formConfig}
      apiEndpoint={apiEndpoint}
      customGridCells={customGridCells}
      searchOnInit={searchOnInit}
      defaultExportParams={defaultExportParams}
    />
  );
}

export default CompanyReport;
